import React, { lazy, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Outlet, Route, Routes } from 'react-router';

import { CLIENT_ROUTES, getFullClientRoute } from '../Shared/routes/routes';
import SuspenseRoute from '../Shared/routes/suspense-route';
import NotFound from '../Shared/widgets/errors/404';
import ClientLayout from './Layout/layout';
import { AuthProvider, ProtectedRoute } from './shared/providers';
import { registerClientServiceWorker } from './shared/services/register-sw';
import { WebViewBridgeProvider, isInWebView } from './shared/webview';

// PWA-установка предлагается ТОЛЬКО в клиентском модуле: манифест линкуем здесь,
// а не глобально в index.tsx. В нативном WebView установка не нужна (это уже
// нативное приложение), поэтому под гейтом `!isInWebView()`. В dev не линкуем —
// service worker (условие установки) регистрируется только в production.
const isPwaEnabled = process.env.NODE_ENV === 'production' && !isInWebView();

const ClientAutoCreditRequests = lazy(
  () => import('./AutoCredit/Requests/requests')
);
const ClientAutoCreditCreate = lazy(
  () => import('./AutoCredit/CreateOrder/create-order')
);
const ClientAutoCreditOffers = lazy(() => import('./AutoCredit/Offers/offers'));

declare global {
  interface Window {
    __gaInitialized?: boolean;
    __ymInitialized?: boolean;
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    ym?: ((
      id: number,
      method: string,
      params?: Record<string, unknown>
    ) => void) & {
      a?: unknown[][];
      l?: number;
    };
  }
}

const ClientAppProvider = () => {
  // Регистрируем service worker только при входе в клиентский модуль —
  // PWA (установка + офлайн-кэш) доступна только для Client.
  useEffect(() => {
    registerClientServiceWorker();
  }, []);

  useEffect(() => {
    const existedScript = document.querySelector(
      'script[src*="googletagmanager.com/gtag"]'
    );
    if (
      existedScript ||
      window.__gaInitialized ||
      !window.location.origin.startsWith('https')
    )
      return;
    window.__gaInitialized = true;

    const googleId = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleId}`;
    script.async = true;

    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments);
      }
      window.gtag = gtag;

      window.gtag('js', new Date());
      window.gtag('config', googleId);
    };

    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const existedScript = document.querySelector(
      'script[src*="mc.yandex.ru/metrika"]'
    );
    if (
      existedScript ||
      window.__ymInitialized ||
      !window.location.origin.startsWith('https')
    )
      return;
    window.__ymInitialized = true;

    const counterId = process.env.REACT_APP_YANDEX_METRICA_ID;
    if (!counterId) return;

    const scriptUrl = `https://mc.yandex.ru/metrika/tag.js?id=${counterId}`;

    // Официальный паттерн Яндекса: создаём очередь вызовов до загрузки скрипта.
    // tag.js при загрузке обработает очередь и начнёт отправлять данные.
    window.ym =
      window.ym ||
      function (...args: unknown[]) {
        const q = (window.ym!.a = window.ym!.a || []) as unknown[][];
        q.push(args);
      };
    window.ym.l = 1 * Date.now();

    const script = document.createElement('script');
    script.async = true;
    script.src = scriptUrl;
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    window.ym(Number(counterId), 'init', {
      ssr: true,
      webvisor: true,
      clickmap: true,
      ecommerce: 'dataLayer',
      referrer: document.referrer,
      url: location.href,
      accurateTrackBounce: true,
      trackLinks: true
    });
  }, []);

  return (
    <>
      {isPwaEnabled && (
        <Helmet>
          <link rel="manifest" href="/site.webmanifest" />
        </Helmet>
      )}
      <WebViewBridgeProvider>
        <Outlet />
      </WebViewBridgeProvider>
    </>
  );
};

const ClientRoutes: React.FC = () => {
  const { i18n, t } = useTranslation();

  return (
    <Routes>
      <Route element={<ClientAppProvider />}>
        <Route element={<AuthProvider />}>
          <Route element={<ClientLayout />}>
            <Route element={<SuspenseRoute loadingType="fullscreen" />}>
              <Route
                path={CLIENT_ROUTES.autoCreditCreate}
                element={<ClientAutoCreditCreate />}
              />

              <Route element={<ProtectedRoute />}>
                <Route
                  path={CLIENT_ROUTES.autoCredit}
                  element={<ClientAutoCreditRequests />}
                />
                <Route
                  path={CLIENT_ROUTES.autoCreditRequest()}
                  element={<ClientAutoCreditOffers />}
                />
              </Route>

              <Route
                path="*"
                element={
                  <div className="client-layout__error">
                    <NotFound
                      shouldCheckMenu={false}
                      redirect={{
                        text: t('client.go-to-creation'),
                        href: `/${i18n.language}${getFullClientRoute(CLIENT_ROUTES.autoCreditCreate)}`
                      }}
                    />
                  </div>
                }
              />
            </Route>
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

export default ClientRoutes;
